import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, MoreHorizontal, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api, ChatChannelResponse, ChatChannelMemberResponse } from '@/lib/api';
import { useArcheAvailability } from '@/hooks/useArcheAvailability';
import { seededGradient } from '../utils';
import { ChannelSettingsModal } from './ChannelSettingsModal';

interface ChannelHeaderProps {
  channel: ChatChannelResponse | null;
  memberCount?: number;
  currentUserId?: string | null;
  members?: ChatChannelMemberResponse[];
  onBack?: () => void;
  onChannelLeft?: () => void;
  onChannelDeleted?: () => void;
}

export function ChannelHeader({
  channel,
  memberCount,
  currentUserId,
  members,
  onBack,
  onChannelLeft,
  onChannelDeleted,
}: ChannelHeaderProps) {
  const guildsAvailable = useArcheAvailability('arche_guilds');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [resolvedAvatarUrl, setResolvedAvatarUrl] = useState<string | null>(
    channel?.imageRef ?? null,
  );

  useEffect(() => {
    setResolvedAvatarUrl(channel?.imageRef ?? null);
    if (!channel?.imageRef && channel?.avatarFileId) {
      api
        .getDownloadUrl(channel.avatarFileId)
        .then((r) => setResolvedAvatarUrl(r.downloadUrl))
        .catch(() => {});
    }
  }, [channel?.channelId, channel?.imageRef, channel?.avatarFileId]);

  if (!channel) {
    return (
      <div className="h-14 border-b border-border bg-card px-4 flex items-center">
        <span className="text-sm text-muted-foreground">Select a channel</span>
      </div>
    );
  }

  // For DM channels, prefer dmPartnerDisplayName from the server
  let dmPartnerName: string | null = null;
  if (channel.channelType === 'direct') {
    dmPartnerName = channel.dmPartnerDisplayName ?? channel.dmPartnerId ?? null;
    if (!dmPartnerName && currentUserId && members && members.length > 0) {
      const partner = members.find((m) => m.userId !== currentUserId);
      if (partner) dmPartnerName = partner.userId;
    }
  }

  const displayName =
    channel.channelType === 'direct'
      ? dmPartnerName || channel.name || 'Direct Message'
      : channel.name || 'Unnamed Channel';

  return (
    <div className="h-14 border-b border-border bg-card px-4 flex items-center justify-between h-[55px]">
      <div className="flex items-center gap-3">
        {onBack && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className="md:hidden h-8 w-8 -ml-1 flex-shrink-0"
            aria-label="Back to channels"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
        )}
        <div
          className="h-8 w-8 rounded-full flex-shrink-0 overflow-hidden flex items-center justify-center"
          style={!resolvedAvatarUrl ? { background: seededGradient(displayName) } : undefined}
        >
          {resolvedAvatarUrl ? (
            <img src={resolvedAvatarUrl} alt={displayName} className="w-full h-full object-cover" />
          ) : (
            <span className="text-sm font-medium text-white">
              {channel.channelType === 'direct' ? displayName.charAt(0).toUpperCase() : '#'}
            </span>
          )}
        </div>
        <div>
          <h2 className="text-sm font-semibold text-foreground">{displayName}</h2>
          {channel.description && (
            <p className="text-xs text-muted-foreground truncate max-w-xs">{channel.description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        {channel.channelType === 'guild' &&
          channel.guildId &&
          (guildsAvailable ? (
            <Link to={`/a/arche_guilds/${channel.guildId}`}>
              <Button variant="ghost" size="sm">
                <Shield className="h-4 w-4 mr-1" />
                View Guild
              </Button>
            </Link>
          ) : (
            <Button variant="ghost" size="sm" disabled>
              <Shield className="h-4 w-4 mr-1" />
              View Guild
            </Button>
          ))}
        {memberCount !== undefined && (
          <span>
            {memberCount} member{memberCount !== 1 ? 's' : ''}
          </span>
        )}
        {(channel.channelType === 'group' || channel.channelType === 'direct') && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSettingsOpen(true)}
            className="h-8 w-8"
            aria-label="Channel settings"
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        )}
      </div>
      {(channel.channelType === 'group' || channel.channelType === 'direct') && (
        <ChannelSettingsModal
          channel={channel}
          members={members ?? []}
          currentUserId={currentUserId}
          open={settingsOpen}
          onOpenChange={setSettingsOpen}
          onLeft={onChannelLeft}
          onDeleted={onChannelDeleted}
        />
      )}
    </div>
  );
}
